'use client'

import {useCallback, useEffect, useMemo, useState} from 'react'
import {Check, ChevronDown, ChevronRight, Plus, Tags, Trash2, Webhook, X} from 'lucide-react'
import type {Account, Contact, ContactLabel} from '@/types'
import type {AccountItem} from '@/hooks/useAccounts'
import {apiGet} from '@/lib/api'
import {avatarFor, displayName} from '@/lib/utils'
import {PageHeader} from '@/components/PageHeader'
import {Switch} from '@/components/Switch'
import {ContactSelectorPanel} from '@/components/ContactSelectorPanel'
import {message} from '@/components/Toast'
import {
    createWebhookApi,
    deleteWebhookApi,
    fetchDeliveryLogs,
    fetchWebhooks,
    toggleWebhookEnabled,
    updateWebhookApi,
} from '@/lib/webhook-api'
import type {
    DeliveryRecord,
    WebhookConfig,
    WebhookEventScope,
} from '@/lib/webhook-api'

type Props = {
    account: Account | null
    accounts: AccountItem[]
    contacts: Contact[]
    groups: Contact[]
    labels: ContactLabel[]
}

type FormState = {
    accountWxid: string
    url: string
    secret: string
    privateMessage: boolean
    privateMessageScope: 'all' | 'selected'
    privateMessageWxids: string[]
    privateMessageLabelIds: number[]
    groupMessage: boolean
    groupMessageScope: 'all' | 'selected'
    groupMessageWxids: string[]
    friendRequest: boolean
}

const emptyForm: FormState = {
    accountWxid: '',
    url: '',
    secret: '',
    privateMessage: false,
    privateMessageScope: 'all',
    privateMessageWxids: [],
    privateMessageLabelIds: [],
    groupMessage: false,
    groupMessageScope: 'all',
    groupMessageWxids: [],
    friendRequest: false,
}

function formFromConfig(config: WebhookConfig, fallbackAccountWxid: string): FormState {
    const pm = config.events.find((e) => e.type === 'private_message')
    const gm = config.events.find((e) => e.type === 'group_message')
    const fr = config.events.find((e) => e.type === 'friend_request')
    return {
        accountWxid: config.accountWxid || fallbackAccountWxid,
        url: config.url,
        secret: config.secret || '',
        privateMessage: !!pm,
        privateMessageScope: pm?.scope || 'all',
        privateMessageWxids: pm?.contactWxids || [],
        privateMessageLabelIds: pm?.labelIds || [],
        groupMessage: !!gm,
        groupMessageScope: gm?.scope || 'all',
        groupMessageWxids: gm?.contactWxids || [],
        friendRequest: !!fr,
    }
}

function formToEvents(form: FormState): WebhookEventScope[] {
    const events: WebhookEventScope[] = []
    if (form.privateMessage) {
        events.push({
            type: 'private_message',
            scope: form.privateMessageScope,
            ...(form.privateMessageScope === 'selected' && {
                contactWxids: form.privateMessageWxids,
                labelIds: form.privateMessageLabelIds,
            }),
        })
    }
    if (form.groupMessage) {
        events.push({
            type: 'group_message',
            scope: form.groupMessageScope,
            ...(form.groupMessageScope === 'selected' && {
                contactWxids: form.groupMessageWxids,
            }),
        })
    }
    if (form.friendRequest) {
        events.push({type: 'friend_request', scope: 'all'})
    }
    return events
}

function isValidUrl(url: string): boolean {
    if (!url) return false
    try {
        const parsed = new URL(url)
        return parsed.protocol === 'http:' || parsed.protocol === 'https:'
    } catch {
        return false
    }
}

function eventSummary(events: WebhookEventScope[]): string {
    const labels: string[] = []
    for (const e of events) {
        if (e.type === 'private_message') labels.push('私聊')
        if (e.type === 'group_message') labels.push('群聊')
        if (e.type === 'friend_request') labels.push('好友请求')
    }
    return labels.join('、') || '无事件'
}

/* ─── Custom Checkbox ─── */
function WebhookCustomCheckbox({
    checked,
    label,
    onChange,
}: {
    checked: boolean
    label: string
    onChange: (checked: boolean) => void
}) {
    return (
        <div
            className="flex items-center gap-2.5 cursor-pointer group select-none"
            onClick={() => onChange(!checked)}
        >
            <div
                className={`w-[18px] h-[18px] rounded-[4px] flex items-center justify-center transition-all border ${
                    checked
                        ? 'bg-[#07c160] border-[#07c160] shadow-[0_2px_4px_rgba(7,193,96,0.2)]'
                        : 'border-gray-300 bg-white group-hover:border-[#07c160]'
                }`}
            >
                {checked && <Check size={12} className="text-white" strokeWidth={4}/>}
            </div>
            <span className={`text-[15px] transition-colors ${checked ? 'text-gray-900 font-semibold' : 'text-gray-700 font-medium group-hover:text-gray-900'}`}>
                {label}
            </span>
        </div>
    )
}

/* ─── Custom Radio ─── */
function WebhookCustomRadio({
    checked,
    label,
    name,
    onChange,
}: {
    checked: boolean
    label: string
    name: string
    onChange: () => void
}) {
    return (
        <div
            className="flex items-center gap-2 cursor-pointer group select-none"
            onClick={onChange}
        >
            <div
                className={`w-[18px] h-[18px] rounded-full flex items-center justify-center transition-all border ${
                    checked
                        ? 'bg-[#07c160] border-[#07c160] shadow-[0_2px_4px_rgba(7,193,96,0.2)]'
                        : 'bg-white border-gray-300 group-hover:border-[#07c160]'
                }`}
            >
                <div className={`w-[8px] h-[8px] rounded-full bg-white transition-transform ${checked ? 'scale-100' : 'scale-0'}`}/>
            </div>
            <span className={`text-sm transition-colors ${checked ? 'text-[#07c160] font-medium' : 'text-gray-600 group-hover:text-gray-800'}`}>
                {label}
            </span>
        </div>
    )
}

/* ─── Main Component ─── */
export function WebhookSettingsView({account, accounts, contacts, groups, labels}: Props) {
    const [webhooks, setWebhooks] = useState<WebhookConfig[]>([])
    const [editing, setEditing] = useState<string | null>(null) // webhook id or 'new'
    const [form, setForm] = useState<FormState>(emptyForm)
    const [urlError, setUrlError] = useState('')
    const [accountError, setAccountError] = useState('')
    const [saving, setSaving] = useState(false)
    const [contactModalOpen, setContactModalOpen] = useState(false)
    const [groupModalOpen, setGroupModalOpen] = useState(false)
    const [scopedContacts, setScopedContacts] = useState<Contact[] | null>(null)
    const [scopedLabels, setScopedLabels] = useState<ContactLabel[] | null>(null)
    const [expandedLogs, setExpandedLogs] = useState<string | null>(null)
    const [deliveryLogs, setDeliveryLogs] = useState<DeliveryRecord[]>([])
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

    const wxid = account?.wxid || ''
    const selectedAccountWxid = form.accountWxid || wxid
    const effectiveContacts = selectedAccountWxid === wxid ? contacts : (scopedContacts || [])
    const effectiveGroups = selectedAccountWxid === wxid ? groups : effectiveContacts.filter((contact) => contact.wxid.includes('@chatroom'))
    const effectiveFriends = selectedAccountWxid === wxid ? contacts : effectiveContacts.filter((contact) => !contact.wxid.includes('@chatroom'))
    const effectiveLabels = selectedAccountWxid === wxid ? labels : (scopedLabels || [])
    const accountOptions = useMemo(() => {
        const seen = new Set<string>()
        const items: Array<{wxid: string; nickName: string; avatar?: string; status?: string}> = []
        if (account?.wxid) {
            seen.add(account.wxid)
            items.push({wxid: account.wxid, nickName: account.nickName || account.wxid, avatar: account.avatar, status: 'online'})
        }
        for (const item of accounts) {
            if (seen.has(item.wxid)) continue
            seen.add(item.wxid)
            items.push(item)
        }
        return items
    }, [account, accounts])

    const loadWebhooks = useCallback(async () => {
        if (!wxid) return
        try {
            const list = await fetchWebhooks(wxid)
            setWebhooks(Array.isArray(list) ? list : [])
        } catch {
            // error already toasted by api layer
        }
    }, [wxid])

    useEffect(() => {
        void loadWebhooks()
    }, [loadWebhooks])

    useEffect(() => {
        if (!editing || !selectedAccountWxid || selectedAccountWxid === wxid) {
            setScopedContacts(null)
            setScopedLabels(null)
            return
        }
        let cancelled = false
        async function loadScopedData() {
            try {
                const [contactsRes, labelsRes] = await Promise.all([
                    apiGet<{success: boolean; contacts: Contact[]}>(`/contacts?wxid=${encodeURIComponent(selectedAccountWxid)}`, {silent: true}),
                    apiGet<{success: boolean; labels: ContactLabel[]}>(`/labels?wxid=${encodeURIComponent(selectedAccountWxid)}`, {silent: true}),
                ])
                if (cancelled) return
                setScopedContacts(contactsRes.contacts || [])
                setScopedLabels(labelsRes.labels || [])
            } catch {
                if (cancelled) return
                setScopedContacts([])
                setScopedLabels([])
            }
        }
        void loadScopedData()
        return () => {
            cancelled = true
        }
    }, [editing, selectedAccountWxid, wxid])

    const handleNew = () => {
        setEditing('new')
        setForm({...emptyForm, accountWxid: wxid || accountOptions[0]?.wxid || ''})
        setUrlError('')
        setAccountError('')
    }

    const handleEdit = (webhook: WebhookConfig) => {
        setEditing(webhook.id)
        setForm(formFromConfig(webhook, wxid))
        setUrlError('')
        setAccountError('')
    }

    const handleCancel = () => {
        setEditing(null)
        setForm(emptyForm)
        setUrlError('')
        setAccountError('')
    }

    const handleSave = async () => {
        if (!form.accountWxid.trim()) {
            setAccountError('请选择微信实例')
            return
        }
        if (!form.url.trim()) {
            setUrlError('请输入回调 URL')
            return
        }
        if (!isValidUrl(form.url.trim())) {
            setUrlError('请输入合法的 HTTP/HTTPS URL')
            return
        }
        const events = formToEvents(form)
        if (events.length === 0) {
            message.warning('请至少选择一个订阅事件')
            return
        }
        setSaving(true)
        try {
            if (editing === 'new') {
                await createWebhookApi(wxid, {
                    accountWxid: form.accountWxid.trim(),
                    url: form.url.trim(),
                    secret: form.secret || undefined,
                    events,
                })
                message.success('Webhook 创建成功')
            } else {
                await updateWebhookApi(wxid, editing!, {
                    accountWxid: form.accountWxid.trim(),
                    url: form.url.trim(),
                    secret: form.secret || undefined,
                    events,
                })
                message.success('Webhook 更新成功')
            }
            setEditing(null)
            setForm(emptyForm)
            await loadWebhooks()
        } catch {
            // error already toasted
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async (id: string) => {
        try {
            await deleteWebhookApi(wxid, id)
            message.success('Webhook 已删除')
            if (editing === id) {
                setEditing(null)
                setForm(emptyForm)
            }
            await loadWebhooks()
        } catch {
            // error already toasted
        }
        setDeleteConfirm(null)
    }

    const handleToggle = async (webhook: WebhookConfig) => {
        try {
            await toggleWebhookEnabled(wxid, webhook.id, !webhook.enabled)
            await loadWebhooks()
        } catch {
            // error already toasted
        }
    }

    const handleExpandLogs = async (webhookId: string) => {
        if (expandedLogs === webhookId) {
            setExpandedLogs(null)
            return
        }
        setExpandedLogs(webhookId)
        try {
            const logs = await fetchDeliveryLogs(wxid, webhookId)
            setDeliveryLogs(Array.isArray(logs) ? logs.slice(0, 20) : [])
        } catch {
            setDeliveryLogs([])
        }
    }

    const hasEvents = form.privateMessage || form.groupMessage || form.friendRequest
    const canSave = form.accountWxid.trim() && form.url.trim() && hasEvents && !saving

    return (
        <section className="flex flex-col h-full bg-[#f5f5f5]">
            <PageHeader
                title="Webhook 触发器"
                description="将微信消息和事件实时推送到你的自有服务器，触发自定义脚本或系统自动化。"
                icon={<Webhook size={20}/>}
                action={
                    !editing ? (
                        <button
                            type="button"
                            onClick={handleNew}
                            className="flex items-center gap-2 bg-[#0bb858] hover:bg-[#06ad56] text-white px-5 py-2.5 rounded-md shadow-sm transition-colors text-sm font-medium"
                        >
                            <Plus size={16}/>
                            新建 Webhook
                        </button>
                    ) : null
                }
            />
            <div className="flex-1 overflow-y-auto p-6">
                <div className="w-full max-w-4xl mr-auto space-y-6">
                    {/* Webhook List */}
                    {webhooks.length > 0 && !editing && (
                        <div className="space-y-3">
                            {webhooks.map((wh) => (
                                <WebhookListItem
                                    key={wh.id}
                                    webhook={wh}
                                    accounts={accountOptions}
                                    expanded={expandedLogs === wh.id}
                                    deliveryLogs={expandedLogs === wh.id ? deliveryLogs : []}
                                    onEdit={() => handleEdit(wh)}
                                    onToggle={() => handleToggle(wh)}
                                    onDelete={() => setDeleteConfirm(wh.id)}
                                    onExpandLogs={() => handleExpandLogs(wh.id)}
                                />
                            ))}
                        </div>
                    )}

                    {/* Form */}
                    {editing && (
                        <WebhookForm
                            form={form}
                            setForm={setForm}
                            urlError={urlError}
                            setUrlError={setUrlError}
                            accountError={accountError}
                            setAccountError={setAccountError}
                            isNew={editing === 'new'}
                            canSave={!!canSave}
                            saving={saving}
                            accounts={accountOptions}
                            contacts={effectiveFriends}
                            groups={effectiveGroups}
                            labels={effectiveLabels}
                            contactModalOpen={contactModalOpen}
                            setContactModalOpen={setContactModalOpen}
                            groupModalOpen={groupModalOpen}
                            setGroupModalOpen={setGroupModalOpen}
                            onSave={handleSave}
                            onCancel={handleCancel}
                        />
                    )}
                </div>
            </div>

            {/* Delete confirmation dialog */}
            {deleteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full mx-4">
                        <h3 className="text-base font-medium text-gray-800 mb-2">确认删除</h3>
                        <p className="text-sm text-gray-500 mb-6">删除后无法恢复，确定要删除这个 Webhook 吗？</p>
                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setDeleteConfirm(null)}
                                className="px-4 py-2 text-sm text-gray-500 hover:text-gray-800 rounded border border-gray-200"
                            >
                                取消
                            </button>
                            <button
                                type="button"
                                onClick={() => handleDelete(deleteConfirm)}
                                className="px-4 py-2 text-sm text-white bg-red-500 hover:bg-red-600 rounded"
                            >
                                删除
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    )
}

/* ─── StatusDot ─── */
function StatusDot({webhook, logs}: {webhook: WebhookConfig; logs: DeliveryRecord[]}) {
    if (webhook.disabledReason) {
        return <span className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" title="已因连续失败自动禁用"/>
    }
    if (!webhook.enabled) {
        return <span className="w-2.5 h-2.5 rounded-full bg-gray-300 shrink-0" title="已禁用"/>
    }
    if (logs.length > 0) {
        const latest = logs[0]
        if (latest.success) {
            return <span className="w-2.5 h-2.5 rounded-full bg-green-500 shrink-0" title="最近投递成功"/>
        }
        return <span className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" title="最近投递失败"/>
    }
    return <span className="w-2.5 h-2.5 rounded-full bg-gray-300 shrink-0" title="无投递记录"/>
}

/* ─── WebhookListItem ─── */
function WebhookListItem({
    webhook,
    accounts,
    expanded,
    deliveryLogs,
    onEdit,
    onToggle,
    onDelete,
    onExpandLogs,
}: {
    webhook: WebhookConfig
    accounts: Array<{wxid: string; nickName: string}>
    expanded: boolean
    deliveryLogs: DeliveryRecord[]
    onEdit: () => void
    onToggle: () => void
    onDelete: () => void
    onExpandLogs: () => void
}) {
    const accountName = accounts.find((item) => item.wxid === webhook.accountWxid)?.nickName || webhook.accountWxid || '未选择实例'

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex items-center gap-3 p-4">
                <StatusDot webhook={webhook} logs={deliveryLogs}/>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={onEdit}>
                    <div className="text-sm font-medium text-gray-800 truncate">{webhook.url}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                        <span className="mr-2">{accountName}</span>
                        {eventSummary(webhook.events)}
                        {webhook.disabledReason && (
                            <span className="ml-2 text-red-500">（{webhook.disabledReason}）</span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={onExpandLogs}
                        className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                        title="投递记录"
                    >
                        {expanded ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                    </button>
                    <Switch checked={webhook.enabled} onChange={onToggle}/>
                    <button
                        type="button"
                        onClick={onDelete}
                        className="p-1.5 rounded text-gray-400 hover:bg-red-50 hover:text-red-500"
                        title="删除"
                    >
                        <Trash2 size={16}/>
                    </button>
                </div>
            </div>
            {expanded && (
                <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/50">
                    <div className="text-xs text-gray-500 mb-2">最近投递记录</div>
                    {deliveryLogs.length === 0 ? (
                        <div className="text-xs text-gray-400 py-2">暂无投递记录</div>
                    ) : (
                        <div className="space-y-1.5 max-h-60 overflow-y-auto">
                            {deliveryLogs.map((log) => (
                                <DeliveryLogRow key={log.id} log={log}/>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

/* ─── DeliveryLogRow ─── */
function DeliveryLogRow({log}: {log: DeliveryRecord}) {
    const [showDetail, setShowDetail] = useState(false)
    const eventLabel =
        log.event === 'private_message' ? '私聊' :
            log.event === 'group_message' ? '群聊' : '好友请求'
    const time = new Date(log.timestamp).toLocaleString('zh-CN', {
        month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    })

    return (
        <div>
            <div
                className={`flex items-center gap-3 text-xs py-1.5 px-2 rounded ${!log.success ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                onClick={() => !log.success && setShowDetail(!showDetail)}
            >
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${log.success ? 'bg-green-500' : 'bg-red-500'}`}/>
                <span className="text-gray-500 w-12 shrink-0">{eventLabel}</span>
                <span className="text-gray-500 w-28 shrink-0">{time}</span>
                <span className={`w-10 shrink-0 ${log.success ? 'text-green-600' : 'text-red-500'}`}>
                    {log.httpStatus || '—'}
                </span>
                {log.retryCount > 0 && (
                    <span className="text-gray-400">重试 {log.retryCount} 次</span>
                )}
            </div>
            {showDetail && !log.success && log.error && (
                <div className="ml-6 px-2 py-1.5 text-xs text-red-600 bg-red-50 rounded mt-1">
                    {log.error}
                    {log.lastRetryAt && (
                        <span className="ml-2 text-gray-400">
                            最后重试: {new Date(log.lastRetryAt).toLocaleString('zh-CN')}
                        </span>
                    )}
                </div>
            )}
        </div>
    )
}

/* ─── WebhookForm ─── */
function WebhookForm({
    form,
    setForm,
    urlError,
    setUrlError,
    accountError,
    setAccountError,
    isNew,
    canSave,
    saving,
    accounts,
    contacts,
    groups,
    labels,
    contactModalOpen,
    setContactModalOpen,
    groupModalOpen,
    setGroupModalOpen,
    onSave,
    onCancel,
}: {
    form: FormState
    setForm: (f: FormState) => void
    urlError: string
    setUrlError: (e: string) => void
    accountError: string
    setAccountError: (e: string) => void
    isNew: boolean
    canSave: boolean
    saving: boolean
    accounts: Array<{wxid: string; nickName: string; avatar?: string; status?: string}>
    contacts: Contact[]
    groups: Contact[]
    labels: ContactLabel[]
    contactModalOpen: boolean
    setContactModalOpen: (v: boolean) => void
    groupModalOpen: boolean
    setGroupModalOpen: (v: boolean) => void
    onSave: () => void
    onCancel: () => void
}) {
    const hasEvents = form.privateMessage || form.groupMessage || form.friendRequest

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {/* Form title section */}
            <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                <div className="flex items-center gap-2.5">
                    <Webhook size={20} className="text-[#07c160]"/>
                    <h3 className="text-lg font-medium text-gray-800">
                        {isNew ? '新建 Webhook' : '编辑 Webhook'}
                    </h3>
                </div>
                <button
                    type="button"
                    onClick={onCancel}
                    className="text-gray-400 hover:text-gray-700 text-sm"
                >
                    取消
                </button>
            </div>

            <div className="space-y-6 mt-6">
                {/* WeChat instance */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">微信实例</label>
                    <select
                        className={`w-full border rounded p-3 text-sm bg-white focus:outline-none focus:border-[#07c160] focus:ring-1 focus:ring-[#07c160] transition-colors ${
                            accountError ? 'border-red-300 bg-red-50/30' : 'border-gray-300'
                        }`}
                        value={form.accountWxid}
                        onChange={(e) => {
                            setForm({
                                ...form,
                                accountWxid: e.target.value,
                                privateMessageWxids: [],
                                privateMessageLabelIds: [],
                                groupMessageWxids: [],
                            })
                            if (accountError) setAccountError('')
                        }}
                    >
                        <option value="">请选择微信实例</option>
                        {accounts.map((item) => (
                            <option key={item.wxid} value={item.wxid}>
                                {item.nickName || item.wxid}（{item.wxid}）
                            </option>
                        ))}
                    </select>
                    {accountError && <p className="text-xs text-red-500 mt-1">{accountError}</p>}
                </div>

                {/* URL */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">回调 URL (Endpoint)</label>
                    <input
                        type="text"
                        className={`w-full border rounded p-3 text-sm focus:outline-none focus:border-[#07c160] focus:ring-1 focus:ring-[#07c160] transition-colors ${
                            urlError ? 'border-red-300 bg-red-50/30' : 'border-gray-300'
                        }`}
                        placeholder="https://api.yourdomain.com/webhook"
                        value={form.url}
                        onChange={(e) => {
                            setForm({...form, url: e.target.value})
                            if (urlError) setUrlError('')
                        }}
                    />
                    {urlError && <p className="text-xs text-red-500 mt-1">{urlError}</p>}
                </div>

                {/* Secret */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">签名密钥 (Secret)</label>
                    <input
                        type="text"
                        className="w-full border border-gray-300 rounded p-3 text-sm focus:outline-none focus:border-[#07c160] focus:ring-1 focus:ring-[#07c160] transition-colors"
                        placeholder="可选，用于 HMAC-SHA256 签名验证"
                        value={form.secret}
                        onChange={(e) => setForm({...form, secret: e.target.value})}
                    />
                    <p className="text-xs text-gray-400 mt-1">
                        配置后，每次推送会在 X-Webhook-Signature 请求头中附带 HMAC-SHA256 签名，接收端可用此密钥验证请求来源。
                    </p>
                </div>

                {/* Events */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">订阅事件</label>
                    <div className="space-y-4">
                        {/* Private message event card */}
                        <div className={`rounded-xl border p-4 transition-all ${
                            form.privateMessage
                                ? 'bg-[#f6fcf8] border-[#a1e5c2] shadow-sm'
                                : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}>
                            <WebhookCustomCheckbox
                                checked={form.privateMessage}
                                label="收到私聊消息"
                                onChange={(checked) => setForm({...form, privateMessage: checked})}
                            />
                            {form.privateMessage && (
                                <div className="ml-7 mt-3 space-y-3">
                                    <div className="flex items-center gap-4">
                                        <WebhookCustomRadio
                                            checked={form.privateMessageScope === 'all'}
                                            label="全部好友"
                                            name="pm-scope"
                                            onChange={() => setForm({...form, privateMessageScope: 'all'})}
                                        />
                                        <WebhookCustomRadio
                                            checked={form.privateMessageScope === 'selected'}
                                            label="指定好友"
                                            name="pm-scope"
                                            onChange={() => setForm({...form, privateMessageScope: 'selected'})}
                                        />
                                    </div>
                                    {form.privateMessageScope === 'selected' && (
                                        <div className="space-y-3">
                                            <button
                                                type="button"
                                                onClick={() => setContactModalOpen(true)}
                                                className="text-[#07c160] bg-white px-3 py-1.5 rounded border border-[#07c160]/40 hover:bg-[#ebf8f0] text-xs font-medium transition-colors"
                                            >
                                                <span className="inline-flex items-center gap-1">
                                                    <Plus size={12}/>
                                                    选择指定好友
                                                </span>
                                            </button>
                                            <SelectedChips
                                                wxids={form.privateMessageWxids}
                                                labelIds={form.privateMessageLabelIds}
                                                contacts={contacts}
                                                labels={labels}
                                                onRemoveWxid={(wxid) =>
                                                    setForm({...form, privateMessageWxids: form.privateMessageWxids.filter((w) => w !== wxid)})
                                                }
                                                onRemoveLabel={(id) =>
                                                    setForm({...form, privateMessageLabelIds: form.privateMessageLabelIds.filter((l) => l !== id)})
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Group message event card */}
                        <div className={`rounded-xl border p-4 transition-all ${
                            form.groupMessage
                                ? 'bg-[#f6fcf8] border-[#a1e5c2] shadow-sm'
                                : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}>
                            <WebhookCustomCheckbox
                                checked={form.groupMessage}
                                label="收到群聊消息"
                                onChange={(checked) => setForm({...form, groupMessage: checked})}
                            />
                            {form.groupMessage && (
                                <div className="ml-7 mt-3 space-y-3">
                                    <div className="flex items-center gap-4">
                                        <WebhookCustomRadio
                                            checked={form.groupMessageScope === 'all'}
                                            label="全部群聊"
                                            name="gm-scope"
                                            onChange={() => setForm({...form, groupMessageScope: 'all'})}
                                        />
                                        <WebhookCustomRadio
                                            checked={form.groupMessageScope === 'selected'}
                                            label="指定群聊"
                                            name="gm-scope"
                                            onChange={() => setForm({...form, groupMessageScope: 'selected'})}
                                        />
                                    </div>
                                    {form.groupMessageScope === 'selected' && (
                                        <div className="space-y-3">
                                            <button
                                                type="button"
                                                onClick={() => setGroupModalOpen(true)}
                                                className="text-[#07c160] bg-white px-3 py-1.5 rounded border border-[#07c160]/40 hover:bg-[#ebf8f0] text-xs font-medium transition-colors"
                                            >
                                                <span className="inline-flex items-center gap-1">
                                                    <Plus size={12}/>
                                                    选择指定群聊
                                                </span>
                                            </button>
                                            <GroupChips
                                                wxids={form.groupMessageWxids}
                                                groups={groups}
                                                onRemove={(wxid) =>
                                                    setForm({...form, groupMessageWxids: form.groupMessageWxids.filter((w) => w !== wxid)})
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Friend request event card */}
                        <div className={`rounded-xl border p-4 transition-all ${
                            form.friendRequest
                                ? 'bg-[#f6fcf8] border-[#a1e5c2] shadow-sm'
                                : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}>
                            <WebhookCustomCheckbox
                                checked={form.friendRequest}
                                label="好友添加请求"
                                onChange={(checked) => setForm({...form, friendRequest: checked})}
                            />
                        </div>
                    </div>
                    {!hasEvents && (
                        <p className="text-xs text-red-500 mt-2">请至少选择一个订阅事件</p>
                    )}
                </div>

                {/* Save button */}
                <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-6 mt-4">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2.5 text-sm text-gray-500 hover:text-gray-800 rounded-md border border-gray-200"
                    >
                        取消
                    </button>
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={!canSave}
                        className="bg-[#0bb858] hover:bg-[#06ad56] disabled:opacity-50 disabled:cursor-not-allowed text-white px-8 py-2.5 rounded-md shadow-sm text-sm font-medium transition-colors"
                    >
                        {saving ? '保存中...' : '保存配置'}
                    </button>
                </div>
            </div>

            {/* Contact selector modal */}
            {contactModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-xl shadow-2xl w-[420px] h-[550px] flex flex-col mx-4">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50/80 rounded-t-xl shrink-0">
                            <h4 className="text-sm font-medium text-gray-800">选择指定好友</h4>
                            <button type="button" onClick={() => setContactModalOpen(false)} className="text-gray-400 hover:text-gray-700">
                                <X size={18}/>
                            </button>
                        </div>
                        <div className="flex-1 overflow-hidden min-h-0">
                            <ContactSelectorPanel
                                className="w-full h-full bg-white flex flex-col overflow-hidden relative"
                                contacts={contacts}
                                selectedWxids={form.privateMessageWxids}
                                onSelect={(wxid) => setForm({...form, privateMessageWxids: [...form.privateMessageWxids, wxid]})}
                                onRemove={(wxid) => setForm({...form, privateMessageWxids: form.privateMessageWxids.filter((w) => w !== wxid)})}
                                onClear={() => setForm({...form, privateMessageWxids: [], privateMessageLabelIds: []})}
                                labels={labels}
                                selectedLabelIds={form.privateMessageLabelIds}
                                onLabelSelect={(id) => setForm({...form, privateMessageLabelIds: [...form.privateMessageLabelIds, id]})}
                                onLabelRemove={(id) => setForm({...form, privateMessageLabelIds: form.privateMessageLabelIds.filter((l) => l !== id)})}
                            />
                        </div>
                        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between rounded-b-xl">
                            <span className="text-xs text-[#07c160] font-medium">
                                已选 {form.privateMessageWxids.length} 位好友，{form.privateMessageLabelIds.length} 个标签
                            </span>
                            <button
                                type="button"
                                onClick={() => setContactModalOpen(false)}
                                className="bg-[#0bb858] hover:bg-[#06ad56] text-white px-5 py-2 rounded-md text-sm font-medium transition-colors"
                            >
                                完成选择
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Group selector modal */}
            {groupModalOpen && (
                <GroupSelectorModal
                    groups={groups}
                    selectedWxids={form.groupMessageWxids}
                    onSelect={(wxid) => setForm({...form, groupMessageWxids: [...form.groupMessageWxids, wxid]})}
                    onRemove={(wxid) => setForm({...form, groupMessageWxids: form.groupMessageWxids.filter((w) => w !== wxid)})}
                    onClose={() => setGroupModalOpen(false)}
                />
            )}
        </div>
    )
}

/* ─── SelectedChips ─── */
function SelectedChips({
    wxids,
    labelIds,
    contacts,
    labels,
    onRemoveWxid,
    onRemoveLabel,
}: {
    wxids: string[]
    labelIds: number[]
    contacts: Contact[]
    labels: ContactLabel[]
    onRemoveWxid: (wxid: string) => void
    onRemoveLabel: (id: number) => void
}) {
    if (wxids.length === 0 && labelIds.length === 0) return null
    return (
        <div className="border-l-2 border-[#07c160]/30 pl-4">
            <div className="flex flex-wrap gap-2">
                {labelIds.map((id) => {
                    const label = labels.find((l) => l.id === id)
                    return (
                        <span
                            key={`label-${id}`}
                            className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded px-2 py-1 text-xs text-gray-700 shadow-sm"
                        >
                            <Tags size={11} className="text-blue-500"/>
                            {label?.name || `标签${id}`}
                            <button type="button" onClick={() => onRemoveLabel(id)} className="text-gray-400 hover:text-red-500">
                                <X size={11}/>
                            </button>
                        </span>
                    )
                })}
                {wxids.map((wxid) => {
                    const contact = contacts.find((c) => c.wxid === wxid)
                    const name = contact ? displayName(contact) : wxid
                    return (
                        <span
                            key={wxid}
                            className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded px-2 py-1 text-xs text-gray-700 shadow-sm"
                        >
                            <img src={avatarFor(wxid)} alt="" className="w-4 h-4 rounded"/>
                            {name}
                            <button type="button" onClick={() => onRemoveWxid(wxid)} className="text-gray-400 hover:text-red-500">
                                <X size={11}/>
                            </button>
                        </span>
                    )
                })}
            </div>
        </div>
    )
}

/* ─── GroupChips ─── */
function GroupChips({
    wxids,
    groups,
    onRemove,
}: {
    wxids: string[]
    groups: Contact[]
    onRemove: (wxid: string) => void
}) {
    if (wxids.length === 0) return null
    return (
        <div className="border-l-2 border-[#07c160]/30 pl-4">
            <div className="flex flex-wrap gap-2">
                {wxids.map((wxid) => {
                    const group = groups.find((g) => g.wxid === wxid)
                    const name = group ? displayName(group) : wxid
                    return (
                        <span
                            key={wxid}
                            className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded px-2 py-1 text-xs text-gray-700 shadow-sm"
                        >
                            <img src={avatarFor(wxid)} alt="" className="w-4 h-4 rounded"/>
                            {name}
                            <button type="button" onClick={() => onRemove(wxid)} className="text-gray-400 hover:text-red-500">
                                <X size={11}/>
                            </button>
                        </span>
                    )
                })}
            </div>
        </div>
    )
}

/* ─── GroupSelectorModal ─── */
function GroupSelectorModal({
    groups,
    selectedWxids,
    onSelect,
    onRemove,
    onClose,
}: {
    groups: Contact[]
    selectedWxids: string[]
    onSelect: (wxid: string) => void
    onRemove: (wxid: string) => void
    onClose: () => void
}) {
    const [query, setQuery] = useState('')
    const selectedSet = useMemo(() => new Set(selectedWxids), [selectedWxids])

    const filtered = useMemo(() => {
        if (!query.trim()) return groups
        const q = query.toLowerCase()
        return groups.filter((g) => {
            const name = displayName(g).toLowerCase()
            return name.includes(q) || g.wxid.toLowerCase().includes(q)
        })
    }, [groups, query])

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-xl shadow-2xl w-[420px] h-[550px] flex flex-col mx-4">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50/80 rounded-t-xl">
                    <h4 className="text-sm font-medium text-gray-800">选择指定群聊</h4>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700">
                        <X size={18}/>
                    </button>
                </div>
                <div className="px-4 pt-3">
                    <input
                        type="text"
                        className="w-full border border-gray-300 rounded p-2.5 text-sm focus:outline-none focus:border-[#07c160] focus:ring-1 focus:ring-[#07c160] transition-colors"
                        placeholder="搜索群聊..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-2">
                    {filtered.length === 0 ? (
                        <div className="text-center text-gray-400 text-sm py-6">无匹配群聊</div>
                    ) : (
                        <div className="space-y-1">
                            {filtered.map((group) => {
                                const isSelected = selectedSet.has(group.wxid)
                                return (
                                    <div
                                        key={group.wxid}
                                        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer text-sm transition-colors ${
                                            isSelected ? 'bg-[#f6fcf8] text-[#07c160]' : 'hover:bg-gray-50 text-gray-700'
                                        }`}
                                        onClick={() => isSelected ? onRemove(group.wxid) : onSelect(group.wxid)}
                                    >
                                        <div className={`w-4 h-4 rounded flex items-center justify-center ${
                                            isSelected
                                                ? 'bg-[#07c160] border border-[#07c160]'
                                                : 'border border-gray-300'
                                        }`}>
                                            {isSelected && <Check size={10} className="text-white" strokeWidth={3}/>}
                                        </div>
                                        <img src={avatarFor(group.wxid)} alt="" className="w-6 h-6 rounded"/>
                                        <span className="truncate">{displayName(group)}</span>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
                <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between rounded-b-xl">
                    <span className="text-xs text-[#07c160] font-medium">已选 {selectedWxids.length} 个群聊</span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="bg-[#0bb858] hover:bg-[#06ad56] text-white px-5 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                        完成选择
                    </button>
                </div>
            </div>
        </div>
    )
}
